import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth/react';
import { GoogleIcon } from '@/components/icons/GoogleIcon';
import { AppLogo } from '@/components/icons/AppLogo';
import { getFirebaseAuthErrorCode, getFriendlyAuthErrorMessage } from '@/lib/firebaseAuthError';
import { useSignupTrack } from '@/analytics';
import { useSignupSource, useCloseAuthModal, useOpenSignup } from '@/store/authModalStore';
import { openAccountStatusGateImperative } from '@/store/accountStatusGateStore';
import {
  isNativeWebView,
  requestNativeGoogleSignIn,
  requestNativeAppleSignIn,
  requestNativeFacebookSignIn,
} from '@/lib/native-bridge';
import { SheetBackButton, FieldLabel, FieldErrorText } from './auth-form-ui';
import { fieldInputClasses, primaryCtaClasses, isValidEmail, INVALID_EMAIL_MESSAGE } from './auth-form-style';

const TEST_ID_PREFIX = 'auth-login';

export interface AuthLoginFormProps {
  /** Called when the user clicks the "Sign up here" link to switch forms. */
  onSwitchMode: () => void;
  /** Called when the user clicks the "Forgot Password?" link. */
  onForgotPassword: () => void;
  /** Called by the back chevron; falls back to nothing when not provided. */
  onBack?: () => void;
}

/** Which field (or the general banner) carries the current error. */
interface FormError {
  field: 'email' | 'password' | 'general';
  message: string;
  /** The no-account banner carries a "Sign up" hand-off link. */
  kind?: 'noAccount';
}

function AppleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 384 512" aria-hidden="true">
      <path
        className="fill-brand-apple"
        d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
      />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        className="fill-brand-facebook"
        d="M24 12c0-6.627-5.373-12-12-12S0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854V15.469H7.078V12h3.047V9.356c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.469h-2.796v8.385C19.612 22.954 24 17.99 24 12z"
      />
    </svg>
  );
}

/**
 * Wrong-password-shaped failures. With Firebase email-enumeration protection
 * the API returns the ambiguous `auth/invalid-credential` for both a wrong
 * password and a missing account — either way the design's answer is the same
 * inline error, NOT a hand-off into signup: an email login with no account
 * behind it errors here (ARC-145 log-in designs). Only social logins force a
 * signup, since the provider auto-creates the account.
 */
const WRONG_PASSWORD_CODES = new Set(['auth/wrong-password', 'auth/invalid-credential', 'auth/invalid-login-credentials']);

/** The designs' wrong-password helper, anchored under the password field. */
const WRONG_PASSWORD_MESSAGE = '*Incorrect password. Please try again.';

/**
 * Login form with email/password and social sign-in.
 *
 * Visual styling mirrors {@link SignupStepMethod} so login and signup look
 * like a matching pair when rendered inside the auth bottom sheet. Errors are
 * field-anchored per the ARC-145 designs; a disabled (closed) account opens
 * the account-closed gate instead of erroring inline.
 */
export function AuthLoginForm({ onSwitchMode, onForgotPassword, onBack }: AuthLoginFormProps) {
  const { signInWithEmailAndPassword, signInWithGoogle, isLoading, signInWithApple, signInWithFacebook } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<FormError | null>(null);
  const [loading, setLoading] = useState(false);
  const closeAuthModal = useCloseAuthModal();
  const openSignup = useOpenSignup();
  const track = useSignupTrack();
  const source = useSignupSource() ?? 'login_sheet';
  const { pathname } = useLocation();

  /** Closed account: no inline error — the account-status gate owns the screen. */
  function handleDisabledAccount() {
    closeAuthModal();
    openAccountStatusGateImperative('ACCOUNT_CLOSED');
  }

  async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    if (!isValidEmail(email)) {
      setError({ field: 'email', message: INVALID_EMAIL_MESSAGE });
      return;
    }

    setLoading(true);
    try {
      await signInWithEmailAndPassword(email, password);
      // Success: AuthModal's post-login routing decides what happens next.
    } catch (err) {
      const code = getFirebaseAuthErrorCode(err);
      if (code === 'auth/user-disabled') {
        handleDisabledAccount();
        return;
      }
      if (code === 'auth/user-not-found') {
        setError({ field: 'email', kind: 'noAccount', message: 'No account found with that email.' });
      } else if (WRONG_PASSWORD_CODES.has(code ?? '')) {
        setError({ field: 'password', message: WRONG_PASSWORD_MESSAGE });
      } else if (code === 'auth/invalid-email') {
        setError({ field: 'email', message: INVALID_EMAIL_MESSAGE });
      } else {
        setError({ field: 'general', message: getFriendlyAuthErrorMessage(err, 'Invalid email or password.') });
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSocial(signIn: () => Promise<void>) {
    setError(null);
    setLoading(true);
    try {
      await signIn();
    } catch (err) {
      if (getFirebaseAuthErrorCode(err) === 'auth/user-disabled') {
        handleDisabledAccount();
        return;
      }
      setError({ field: 'general', message: getFriendlyAuthErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }

  function handleGoogle() {
    // In a React Native WebView, Firebase's signInWithPopup is blocked by Google.
    // Delegate to the RN app which opens the system browser for the OAuth flow.
    if (isNativeWebView()) {
      requestNativeGoogleSignIn();
      return;
    }
    void handleSocial(signInWithGoogle);
  }

  function handleApple() {
    if (isNativeWebView()) {
      requestNativeAppleSignIn();
      return;
    }
    void handleSocial(signInWithApple);
  }

  function handleFacebook() {
    if (isNativeWebView()) {
      requestNativeFacebookSignIn();
      return;
    }
    void handleSocial(signInWithFacebook);
  }

  const busy = loading || isLoading;
  const emailError = error?.field === 'email';
  const passwordError = error?.field === 'password';

  return (
    <div data-testid={TEST_ID_PREFIX} className="relative flex flex-col gap-10 w-full max-w-[377px] mx-auto">
      {onBack && <SheetBackButton testId={`${TEST_ID_PREFIX}-back-button`} onClick={onBack} />}
      <div className="flex flex-col items-center">
        <AppLogo width={248} disableAnimation={true} />
      </div>

      {/* noValidate: email/password errors must render as the designs' styled
          field errors — never the browser's native tooltip (Karni, 2026-07-15). */}
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4 w-full max-w-[350px] mx-auto">
        <h2 className="text-lg font-medium text-ink">Log In with:</h2>

        {error?.field === 'general' && (
          <p data-testid={`${TEST_ID_PREFIX}-error`} role="alert" className="text-danger-alt text-base px-[22px]">
            {error.message}
          </p>
        )}

        <div className="flex flex-col gap-[22px] pt-2">
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="login-sheet-email" error={emailError}>
              Email
            </FieldLabel>
            <input
              data-testid={`${TEST_ID_PREFIX}-email-input`}
              id="login-sheet-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              disabled={busy}
              className={fieldInputClasses(emailError)}
            />
            {emailError && (
              <FieldErrorText testId={`${TEST_ID_PREFIX}-email-error`}>
                {error.message}
                {error.kind === 'noAccount' && (
                  <>
                    {' '}
                    <button
                      data-testid={`${TEST_ID_PREFIX}-error-signup-link`}
                      type="button"
                      className="text-purple font-medium underline"
                      onClick={() => openSignup('login_sheet', { prefillEmail: email })}
                    >
                      Sign up
                    </button>
                  </>
                )}
              </FieldErrorText>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="login-sheet-password" error={passwordError}>
              Password
            </FieldLabel>
            <div className="relative">
              <input
                data-testid={`${TEST_ID_PREFIX}-password-input`}
                id="login-sheet-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={busy}
                className={fieldInputClasses(passwordError, 'pr-10')}
              />
              <button
                data-testid={`${TEST_ID_PREFIX}-password-toggle`}
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted opacity-60 hover:opacity-100"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  {showPassword ? (
                    <path
                      d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 15a3 3 0 100-6 3 3 0 000 6z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  ) : (
                    <path
                      d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a19.77 19.77 0 014.06-5.94M9.9 4.24A11 11 0 0112 4c7 0 11 8 11 8a19.77 19.77 0 01-3.16 4.34M1 1l22 22 M14.12 14.12a3 3 0 11-4.24-4.24"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}
                </svg>
              </button>
            </div>
            {passwordError && <FieldErrorText testId={`${TEST_ID_PREFIX}-password-error`}>{error.message}</FieldErrorText>}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1 h-[2px] rounded-pill bg-gradient-to-r from-brand-grad-3 via-brand-grad-2 to-brand-grad-1" />
          <span className="text-lg font-light text-ink">or with</span>
          <div className="flex-1 h-[2px] rounded-pill bg-gradient-to-r from-brand-grad-1 via-brand-grad-2 to-brand-grad-3" />
        </div>

        <div className="flex items-center justify-center gap-[14px]">
          <button
            data-testid={`${TEST_ID_PREFIX}-apple-button`}
            type="button"
            onClick={handleApple}
            disabled={busy}
            className="w-[106px] h-[38px] shrink-0 inline-flex items-center justify-center gap-1.5 rounded-[40px] bg-surface border-2 border-border text-base font-medium text-ink disabled:opacity-60"
          >
            <AppleIcon />
            Apple
          </button>
          <button
            data-testid={`${TEST_ID_PREFIX}-google-button`}
            type="button"
            onClick={handleGoogle}
            disabled={busy}
            className="w-[106px] h-[38px] shrink-0 inline-flex items-center justify-center gap-1.5 rounded-[40px] bg-surface border-2 border-border text-base font-medium text-ink hover:opacity-90 disabled:opacity-50"
          >
            <GoogleIcon />
            Google
          </button>
          <button
            data-testid={`${TEST_ID_PREFIX}-facebook-button`}
            type="button"
            onClick={handleFacebook}
            disabled={busy}
            className="w-[106px] h-[38px] shrink-0 inline-flex items-center justify-center gap-1.5 rounded-[40px] bg-surface border-2 border-border text-sm font-medium text-info disabled:opacity-60"
          >
            <FacebookIcon />
            Facebook
          </button>
        </div>

        <div className="flex flex-col items-center gap-2 mt-8">
          <button
            data-testid={`${TEST_ID_PREFIX}-guest-button`}
            type="button"
            onClick={() => {
              track('guest_mode_entered', { source, page_path: pathname });
              closeAuthModal();
            }}
            className="text-lg font-medium text-purple underline"
          >
            Continue as a guest
          </button>

          <p className="text-base text-ink text-center pt-2">
            Forgot Password?{' '}
            <button
              data-testid={`${TEST_ID_PREFIX}-forgot-button`}
              type="button"
              onClick={onForgotPassword}
              disabled={busy}
              className="text-purple font-medium underline disabled:opacity-50"
            >
              Click here
            </button>
          </p>

          <button
            data-testid={`${TEST_ID_PREFIX}-submit-button`}
            type="submit"
            disabled={busy}
            className={primaryCtaClasses}
          >
            {busy ? 'Signing in…' : 'Log In'}
          </button>
          <p className="text-base text-ink pt-1">
            Don&apos;t have a Crown account yet?{' '}
            <button
              data-testid={`${TEST_ID_PREFIX}-signup-button`}
              type="button"
              className="text-purple font-medium underline"
              onClick={onSwitchMode}
            >
              Sign up here
            </button>
          </p>
        </div>
      </form>
    </div>
  );
}
